import React, { useCallback, useState } from 'react';

import { NoResultsText } from './Autocomplete-styled-components';
import { ApplyButton, CancelButton, DropdownBody, DropdownFooter } from './Dropdown-styled-components';
import { CheckboxOption, Option, OptionProps } from './DropdownOption';
import { AutocompleteOption } from './types';

interface DropdownProps {
  options: AutocompleteOption[];
  filter: string | null;
  hoveredOption: number | string | null;
  handleMouseEnter: (id: OptionProps['option']['id']) => void;
  handleSelect: (id: OptionProps['option']['id']) => void;
}

export const SingleSelectOptions = ({
  options,
  filter,
  hoveredOption,
  handleMouseEnter,
  handleSelect,
}: DropdownProps) => {
  const hasOptionWithDescription = options.some((option) => option.description);

  return (
    <DropdownBody tabIndex={-1}>
      {options?.map((option) => (
        <Option
          key={option.id}
          option={option}
          hoveredOption={hoveredOption}
          // If there is an option with a description, use bold name for all options
          useBoldName={hasOptionWithDescription}
          handleMouseEnter={() => handleMouseEnter(option.id)}
          handleSelect={() => handleSelect(option.id)}
        />
      ))}
      {!options?.length
        && <NoResultsText>No results found for "{filter}"</NoResultsText>}
    </DropdownBody>
  );
};

interface MultiSelectDropdownProps extends DropdownProps {
  handleCancel: () => void;
  initialSelectedOptions: AutocompleteOption[];
}

export const MultiSelectOptions = ({
  options,
  initialSelectedOptions,
  hoveredOption,
  handleMouseEnter,
  handleSelect,
  handleCancel,
}: MultiSelectDropdownProps) => {
  const hasOptionWithDescription = options.some((option) => option.description);

  const [selectedOptions, setSelectedOptions] = useState<AutocompleteOption['id'][]>(
    (initialSelectedOptions || []).map((o) => o.id),
  );

  const handleApply = () => {
    handleSelect(selectedOptions);
  };

  const handleCheck = (optionId: AutocompleteOption['id']) => {
    setSelectedOptions((prev) => {
      const optionsSet = new Set(prev);
      if (optionsSet.has(optionId)) {
        optionsSet.delete(optionId);
      } else {
        optionsSet.add(optionId);
      }
      return Array.from(optionsSet);
    });
  };

  const isChecked = useCallback(
    (optionId: AutocompleteOption['id']) => selectedOptions.includes(optionId),
    [selectedOptions],
  );

  return (
    <>
      <DropdownBody tabIndex={-1}>
        {options?.map((option) => (
          <CheckboxOption
            key={option.id}
            option={option}
            isChecked={isChecked(option.id)}
            hoveredOption={hoveredOption}
            // If there is an option with a description, use bold name for all options
            useBoldName={hasOptionWithDescription}
            handleMouseEnter={() => handleMouseEnter(option.id)}
            handleCheck={() => handleCheck(option.id)}
          />
        ))}
      </DropdownBody>
      <DropdownFooter>
        <CancelButton onClick={handleCancel}>Cancel</CancelButton>
        <ApplyButton onClick={handleApply}>Apply</ApplyButton>
      </DropdownFooter>
    </>
  );
};
